import axios, { AxiosError, AxiosHeaders, InternalAxiosRequestConfig } from "axios";
import { getAccess, getRefresh, saveTokens, clearTokens } from "./tokenStorage";
import { startLoading, stopLoading } from "./loading";
import { emitForceLogout } from "./authEvents";

type RetryConfig = InternalAxiosRequestConfig & { _retry?: boolean };

const baseURL =
  (import.meta as any)?.env?.VITE_API_BASE_URL ||
  process.env.REACT_APP_API_BASE_URL ||
  "https://api.roott.one/api";

const REFRESH_PATH = "/users/api/token/refresh/";
const LOGIN_PATH = "/users/login/";

const api = axios.create({ baseURL, timeout: 60000 });
api.interceptors.request.use((config) => {
  startLoading();
  return config;
});

api.interceptors.response.use(
  (r) => {
    stopLoading();
    return r;
  },
  async (error) => {
    stopLoading();
    const resp = error.response;
    const original = (error.config || {}) as any;
    if (!resp) throw error;
    throw error;
  }
);
const refreshClient = axios.create({ baseURL, timeout: 15000 });
refreshClient.interceptors.request.use((config) => {
  startLoading();
  return config;
});
refreshClient.interceptors.response.use(
  (r) => {
    stopLoading();
    return r;
  },
  (e) => {
    stopLoading();
    return Promise.reject(e);
  }
);

const ensureHeaders = (cfg: InternalAxiosRequestConfig): AxiosHeaders => {
  const h = AxiosHeaders.from(cfg.headers || {});
  cfg.headers = h;
  return h;
};

const normalizeBearer = (token?: string | null) =>
  !token ? null : token.startsWith("Bearer ") ? token : `Bearer ${token}`;

const samePath = (url: string | undefined, path: string) => {
  if (!url) return false;
  const u = url.toLowerCase().replace(/\/+$/, "");
  const p = path.toLowerCase().replace(/\/+$/, "");
  return u.endsWith(p);
};

function setAuthHeader(cfg: InternalAxiosRequestConfig, token: string) {
  const headers = ensureHeaders(cfg);
  headers.set("Authorization", token);
}

api.interceptors.request.use((config) => {
  const headers = ensureHeaders(config);
  headers.set("Accept", "application/json");
  if (!headers.get("Content-Type")) headers.set("Content-Type", "application/json");

  const isAuthCall = samePath(config.url, LOGIN_PATH) || samePath(config.url, REFRESH_PATH);
  if (!isAuthCall) {
    const access = normalizeBearer(getAccess());
    if (access) setAuthHeader(config, access);
  }
  return config;
});

let isRefreshing = false;
let waiters: Array<() => void> = [];
const notifyWaiters = () => {
  waiters.forEach((res) => res());
  waiters = [];
};

api.interceptors.response.use(
  (r) => r,
  async (error: AxiosError) => {
    const resp = error.response;
    const original = (error.config || {}) as RetryConfig;
    if (!resp) throw error;

    const isAuthCall = samePath(original.url, LOGIN_PATH) || samePath(original.url, REFRESH_PATH);

    if (resp.status === 401 && !original._retry && !isAuthCall) {
      original._retry = true;

      if (isRefreshing) {
        await new Promise<void>((res) => waiters.push(res));
        const access = normalizeBearer(getAccess());
        if (!access) throw error;
        setAuthHeader(original, access);
        return api(original);
      }

      isRefreshing = true;
      try {
        const refresh = getRefresh();
        if (!refresh) {
          emitForceLogout("refresh_missing");
          throw new Error("No refresh token available");
        }

        const { data } = await refreshClient.post(REFRESH_PATH, { refresh });

        const nextAccessRaw: string =
          (data as any)?.access ?? (data as any)?.access_token ?? "";

        if (!nextAccessRaw) throw new Error("Refresh did not return access token");

        const nextAccess = normalizeBearer(nextAccessRaw)!;
        const nextRefresh: string = getRefresh() || refresh;

        saveTokens(nextAccess, nextRefresh);

        notifyWaiters();

        setAuthHeader(original, nextAccess);
        return api(original);
      } catch (e) {
        clearTokens();
        notifyWaiters();
        emitForceLogout("refresh_failed");
        throw e;
      } finally {
        isRefreshing = false;
      }
    }

    throw error;
  }
);


export type Option = { value: string; label: string };

const asArray = (d: any) => (Array.isArray(d) ? d : Array.isArray(d?.results) ? d.results : []);

export const getMobileCodes = async (): Promise<Option[]> => {
  const { data } = await api.get("/users/meta/mobile-codes/");
  let entries: [string, string][] = [];
  if (data && typeof data === "object" && !Array.isArray(data)) {
    entries = Object.entries(data as Record<string, string>);
  } else if (Array.isArray(data)) {
    entries = data
      .map((x: any) => [String(x?.name ?? x), String(x?.dial_code ?? x?.code ?? x ?? "")]) as [string, string][];
  }
  const seen = new Set<string>();
  const opts = entries
    .map(([country, code]) => ({ value: code, label: `${country} (${code})` }))
    .filter((o) => {
      if (!o.value) return false;
      if (seen.has(o.value)) return false;
      seen.add(o.value);
      return true;
    })
    .sort((a, b) => a.label.localeCompare(b.label));
  return opts;
};

const toStringList = (data: any): string[] => {
  if (Array.isArray(data)) return data.map((x) => String(x ?? ""));
  if (Array.isArray(data?.results)) return data.results.map((x: any) => String(x ?? ""));
  if (data && typeof data === "object") return Object.values(data).map((x: any) => String(x ?? ""));
  return [];
};

const toOptions = (list: string[], preserveOrder = false): Option[] => {
  const dedup = Array.from(new Set(list.filter(Boolean)));
  if (!preserveOrder) {
    dedup.sort((a, b) => a.localeCompare(b));
  }
  return dedup.map((v) => ({ value: v, label: v }));
};

export const getCountries = async (preserveOrder = false): Promise<Option[]> => {
  const { data } = await api.get("/users/meta/countries/");
  return toOptions(toStringList(data), preserveOrder);
};

export const getStates = async (country?: string): Promise<Option[]> => {
  const { data } = await api.get("/users/meta/states/", {
    params: country ? { country } : {},
  });
  return toOptions(toStringList(data));
};

export const getCities = async (country?: string, state?: string): Promise<Option[]> => {
  const params: Record<string, string> = {};
  if (country) params.country = country;
  if (state) params.state = state;

  const { data } = await api.get("/users/meta/cities/", { params });
  return toOptions(toStringList(data));
};

export const getSectors = async (): Promise<Option[]> => {
  const { data } = await api.get("/users/meta/sectors/");
  return toOptions(toStringList(data));
};

export const getOrgTypes = async (preserveOrder = false): Promise<Option[]> => {
  const { data } = await api.get("/users/meta/org-types/");
  return toOptions(toStringList(data), preserveOrder);
};

export const getOrgSizes = async (): Promise<Option[]> => {
  const { data } = await api.get("/users/meta/org-sizes/");
  return toOptions(toStringList(data));
};

export const getRoles = async (): Promise<Option[]> => {
  const { data } = await api.get("/users/meta/roles/");
  return toOptions(toStringList(data));
};

export const getBeneficiaries = async (preserveOrder = false): Promise<Option[]> => {
  const { data } = await api.get("/users/meta/beneficiaries/");
  return toOptions(toStringList(data), preserveOrder);
};

export const getBudgetRanges = async (preserveOrder = false): Promise<Option[]> => {
  const { data } = await api.get("/users/meta/budget-ranges/");
  return toOptions(toStringList(data), preserveOrder);
};

export const getFundingSources = async (): Promise<Option[]> => {
  const { data } = await api.get("/users/meta/funding-sources/");
  return toOptions(data);
};

export const getFundingTypesMeta = async (preserveOrder = false): Promise<Option[]> => {
  const { data } = await api.get("/users/meta/funding-types/");
  return toOptions(toStringList(data), preserveOrder);
};

export const getYearsOperating = async (preserveOrder = false): Promise<Option[]> => {
  const { data } = await api.get("/users/meta/years-operating/");
  return toOptions(toStringList(data), preserveOrder);
};

export const getFundingAmounts = async (preserveOrder = false): Promise<Option[]> => {
  const { data } = await api.get("/users/meta/funding-amounts/");
  return toOptions(toStringList(data), preserveOrder);
};

export const getFundingTimelines = async (preserveOrder = false): Promise<Option[]> => {
  const { data } = await api.get("/users/meta/funding-timelines/");
  return toOptions(toStringList(data), preserveOrder);
};

export type FocusAreaGroup = {
  label: string;
  options: string[];
};

const toFocusAreaGroups = (data: any): FocusAreaGroup[] => {
  const input = data?.groups ?? data?.categories ?? data;
  const list = Array.isArray(input)
    ? input
    : input && typeof input === "object"
    ? Object.entries(input).map(([label, options]) => ({ label, options }))
    : [];

  const groups = list
    .map((group: any) => {
      const label = String(group?.label ?? group?.name ?? group?.title ?? "").trim();
      const rawOptions = Array.isArray(group?.options)
        ? group.options
        : Array.isArray(group?.items)
        ? group.items
        : Array.isArray(group?.values)
        ? group.values
        : [];
      const options = rawOptions
        .map((item: any) =>
          typeof item === "string"
            ? item
            : String(item?.label ?? item?.name ?? item?.value ?? "").trim()
        )
        .filter(Boolean);
      if (!label || !options.length) return null;
      return { label, options };
    })
    .filter(Boolean) as FocusAreaGroup[];

  return groups;
};

export const getFocusAreasMeta = async (): Promise<{
  groups: FocusAreaGroup[];
  maxSelection?: number;
  minSelection?: number;
  allowCustom?: boolean;
}> => {
  const { data } = await api.get("/users/meta/focus-areas/");
  return {
    groups: toFocusAreaGroups(data),
    maxSelection:
      typeof data?.max_selection === "number"
        ? data.max_selection
        : typeof data?.maxSelected === "number"
        ? data.maxSelected
        : undefined,
    minSelection:
      typeof data?.min_selection === "number"
        ? data.min_selection
        : typeof data?.minSelected === "number"
        ? data.minSelected
        : undefined,
    allowCustom:
      typeof data?.allow_custom === "boolean"
        ? data.allow_custom
        : typeof data?.allowCustom === "boolean"
        ? data.allowCustom
        : undefined,
  };
};

export const getTrackerEvents = async () => {
  const { data } = await api.get("/tracker/events/");
  return data;
};
export const postChangePassword = async (current_password: string, new_password: string, confirm_password: string) => {
  const { data } = await api.post("/users/profile/password-change/", {
    current_password,
    new_password,
    confirm_password,
  });
  return data;
}

export const getNotificationPreferences = async () => {
  const { data } = await api.get("/users/profile/notification-preferences/");
  return data?.notification_preferences || {};
};

export const updateNotificationPreferences = async (payload: {
  email_notifications: {
    account: boolean;
    community: boolean;
    team: boolean;
    grant_updates: boolean;
  };
  app_notifications: {
    account: boolean;
    community: boolean;
    team: boolean;
    grant_updates: boolean;
  };
}) => {
  const { data } = await api.patch("/users/profile/notification-preferences/", {
    notification_preferences: payload,
  });
  return data;
};

export const postSupport = async (payload: {
  category: string,
  description: string,
  email: string,
  name: string,
}) => {
  // console.log(payload);

  const { data } = await api.post("/support/api/tickets/",
    payload,
  );
  return data;
};

export const getCategories = async () => {
  const { data } = await api.get("/support/meta/tickets/categories/");

  return data;
}

// export const postNotification

export const getSdgs = async (): Promise<Option[]> => {
  const { data } = await api.get("/users/meta/sdgs/");
  const arr: any[] = Array.isArray(data) ? data : Array.isArray((data as any)?.results) ? (data as any).results : [];

  const opts = arr
    .map((x) => {
      if (typeof x === "string") return { value: x, label: x };
      const code = String(x?.code ?? x?.value ?? "");
      const label = String(x?.label ?? x?.name ?? code);
      return { value: code || label, label: label || code };
    })
    .filter((o) => o.value && o.label);

  const seen = new Set<string>();
  return opts
    .filter((o) => (seen.has(o.value) ? false : (seen.add(o.value), true)))
    .sort((a, b) => a.label.localeCompare(b.label));
};

export const patchSavedGrantDetails = async (
  id: number,
  payload: {
    outcome?: string | null;
    decision_due?: string | null;
    comments?: string | null;
    total_funding_awarded?: number | null;
  }
) => {
  const { data } = await api.patch(
    `/organizer/saved-grants/${id}/update-details/`,
    payload
  );

  return data;
};
export default api;
